import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { ArrowLeft, ChevronRight, Star, CheckCircle2, Play, Lock } from 'lucide-react';

const TopicPage = () => {
  const { topicId } = useParams();
  const { api } = useAuth();
  const location = useLocation();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  // Detect if we're in admin preview mode
  const isAdminPreview = location.pathname.startsWith('/admin/preview');
  const basePath = isAdminPreview ? '/admin/preview' : '/student';
  const dashboardPath = isAdminPreview ? '/admin' : '/student';

  useEffect(() => {
    const fetchTopic = async () => {
      try {
        const response = await api.get(`/v2/curriculum/topic/${topicId}`);
        setTopic(response.data);
      } catch (error) {
        console.error('Error fetching topic:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTopic();
  }, [topicId, api]);

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </StudentLayout>
    );
  }

  if (!topic) {
    return (
      <StudentLayout>
        <div className="text-center py-12">
          <h2 className="font-heading text-2xl font-bold text-slate-800">Topic not found</h2>
          <Link to={dashboardPath} className="text-purple-600 font-semibold mt-4 inline-block">
            Go back
          </Link>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <Link 
        to={dashboardPath}
        className="inline-flex items-center gap-2 text-slate-600 hover:text-purple-600 font-semibold mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </Link>

      {/* Topic Header */}
      <div 
        className="student-card mb-8"
        style={{ backgroundColor: topic.color + '20', borderColor: topic.color }}
      >
        <h1 className="font-heading text-3xl font-bold text-slate-800 mb-2">{topic.name}</h1>
        <p className="text-slate-600">{topic.description}</p>
        <p className="text-sm text-slate-500 mt-2">{topic.lessons?.length || 0} Lessons</p>
      </div>

      {/* Lessons List */}
      <h2 className="font-heading text-2xl font-bold text-slate-800 mb-4">Lessons</h2>
      <div className="space-y-4">
        {topic.lessons?.map((lesson, index) => {
          const isCompleted = lesson.completed;
          const starsEarned = lesson.stars_earned || 0;

          return (
            <Link 
              key={lesson.id}
              to={`${basePath}/lesson/${lesson.id}`}
              data-testid={`lesson-${index + 1}`}
            >
              <div className={`student-card cursor-pointer group ${
                isCompleted ? 'bg-green-50 border-green-400' : ''
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl border-2 border-gray-800 flex items-center justify-center font-bold text-lg ${
                    isCompleted ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : index + 1}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-bold text-slate-800">
                      {lesson.title || `Lesson ${index + 1}`}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {lesson.objectives?.[0] || 'Practice exercises with instant feedback'}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Stars */}
                    <div className="flex items-center gap-1">
                      {[1, 2, 3].map(s => (
                        <Star 
                          key={s}
                          className={`w-5 h-5 ${s <= starsEarned ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    
                    <div className={`w-10 h-10 rounded-full border-2 border-gray-800 flex items-center justify-center ${
                      isCompleted ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'
                    }`}>
                      {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </StudentLayout>
  );
};

export default TopicPage;
